import { isNil } from 'lodash-es';
import { forwardRef } from 'react';

import type { HealthIndicatorProps } from './health-indicator-types.js';
import { Label } from './Label.js';
import { StatusVisual } from './StatusVisual.js';
import * as styles from './styles.sty.js';
import { Visual } from './Visual.js';
import { useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useId } from '../../core/hooks/useId.js';
import { useSafeForwardProps } from '../../core/hooks/useSafeForwardProps.js';
import { useSlots } from '../../core/hooks/useSlots.js';
import { useContainerColoring } from '../../layouts/container/Container.js';
import { Flex } from '../../layouts/flex/Flex.js';

const COMPONENT_NAME = 'HealthIndicator';

const HealthIndicatorRoot = forwardRef<HTMLDivElement, HealthIndicatorProps>(
  (props, forwardedRef) => {
    const {
      children,
      id,
      status = 'neutral',
      visual = 'shape',
      className: consumerClassName,
      style: consumerStyle,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      'data-testid': dataTestId,
      ...remainingProps
    } = props;

    const containerColoring = useContainerColoring();
    const onAccent = containerColoring.variant === 'accent';

    const [behavioralTrackingProps, restProps] =
      useBehavioralTrackingProps(remainingProps);
    const ariaLabelingProps = useAriaLabelingProps(restProps);
    const forwardProps = useSafeForwardProps(restProps, ariaLabelingProps);
    const {
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      ...remainingAriaLabelingProps
    } = ariaLabelingProps;

    const labelId = useId();
    const [slots] = useSlots(children, {
      visual: Visual,
      label: Label,
    });

    const visualContainerHeight = visual === 'icon' ? 10 : 6;
    const hasLabel = (ariaLabelledBy ?? ariaLabel ?? slots.label) !== undefined;

    return (
      <Flex
        id={id}
        ref={forwardedRef}
        alignItems="flex-start"
        gap={visual === 'icon' ? 8 : 4}
        className={consumerClassName}
        style={{
          minWidth: 0,
          ...consumerStyle,
        }}
        data-testid={dataTestId}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
        {...behavioralTrackingProps}
        {...forwardProps}
        data-dt-component={COMPONENT_NAME}
      >
        <div
          className={styles.visual({ status, onAccent, visualType: visual })}
          role={hasLabel ? 'img' : undefined}
          aria-labelledby={
            ariaLabelledBy ?? (slots.label && !ariaLabel ? labelId : undefined)
          }
          aria-label={ariaLabel}
          {...remainingAriaLabelingProps}
        >
          {!isNil(slots.visual) ? (
            slots.visual
          ) : (
            <StatusVisual status={status} visual={visual} />
          )}
        </div>
        {!isNil(slots.label) && (
          <div
            id={labelId}
            style={{
              // Calculate padding-top: (container height for icon (20px) or shape (12px) - 1 line height) / 2
              paddingTop: `calc(-0.5lh + ${visualContainerHeight}px)`,
              minWidth: '0',
            }}
          >
            {slots.label}
          </div>
        )}
      </Flex>
    );
  },
);

(
  HealthIndicatorRoot as typeof HealthIndicatorRoot & { displayName: string }
).displayName = COMPONENT_NAME;

/**
 * Use the `HealthIndicator` to communicate the status of a system, process, or object using semantic color and visual shape or icon indicators.
 * @public
 */
export const HealthIndicator = Object.assign(HealthIndicatorRoot, {
  Visual,
  Label,
});
